import {useCallback, useEffect, useMemo} from 'react';

import type {CompanyWorkforceProfile} from '../types/WorkforceProfileTypes';
import {
  parseWorkforceProfilesIdsQueryParam,
  WORKFORCE_PROFILES_IDS_QUERY_PARAM,
} from '../utils/WorkforceProfiles/buildWorkforceProfilesListFilterUrl';

import {LocalQueryParams, useQueryParameterState} from '../../../../hooks/useQueryParameterState';

interface WorkforceProfilesQueryParams extends LocalQueryParams {
  wp_ids?: string;
}

interface Options {
  hasNext: boolean;
  isLoading: boolean;
  getNext: () => void;
}

export const useWorkforceProfilesListFilter = (profiles: CompanyWorkforceProfile[], options: Options) => {
  const {updateQueryParameters, params} = useQueryParameterState<WorkforceProfilesQueryParams>();

  const profileIds = useMemo(
    () => parseWorkforceProfilesIdsQueryParam(params[WORKFORCE_PROFILES_IDS_QUERY_PARAM]),
    [params]
  );

  const hasListFilters = profileIds.length > 0;

  const filteredProfiles = useMemo(() => {
    if (!hasListFilters) {
      return profiles;
    }

    const idSet = new Set(profileIds);
    return profiles.filter((profile) => idSet.has(profile.resourceId));
  }, [hasListFilters, profileIds, profiles]);

  useEffect(() => {
    if (hasListFilters && options.hasNext && !options.isLoading) {
      options.getNext();
    }
  }, [hasListFilters, options]);

  const clearFilter = useCallback(() => {
    updateQueryParameters({[WORKFORCE_PROFILES_IDS_QUERY_PARAM]: ''});
  }, [updateQueryParameters]);

  return {
    filteredProfiles,
    hasListFilters,
    filterCount: profileIds.length,
    matchedCount: filteredProfiles.length,
    clearFilter,
    isFetchingFilteredPages: hasListFilters && options.hasNext,
  };
};
